import React, { useEffect, useState } from 'react';
import Button from '../../../components/common/Button';
import Search from '../../../components/common/Table/searchBox';
import { StrongSubtitle } from '../../../components/common/Title';
import HeadingSection from '../../../components/common/Title/headingSection';
import NewsCard from './newsCard';
import { useHistory } from 'react-router-dom';
import NewsApi from '../news-api';
import moment from 'moment';
import Alert from '../../../components/common/Alert';

import NewsModal from '../newsModal';
import Pagination from '@material-ui/lab/Pagination';

const sortNewsByDisplayOrder = (list = []) => {
  return [...list].sort((a, b) => {
    const hasA = typeof a.displayorder === 'number';
    const hasB = typeof b.displayorder === 'number';

    if (hasA && hasB) {
      if (a.displayorder === b.displayorder) {
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }
      return a.displayorder - b.displayorder;
    }

    if (hasA) return -1;
    if (hasB) return 1;

    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
  });
};

const NewsList = () => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [news, setNews] = useState([]);
  const [filteredNews, setFilteredNews] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState('');
  const [modalContent, setModalContent] = useState({
    title: '',
    header: '',
    action: '',
    note: '',
  });
  const [open, setOpen] = useState(false);

  const itemsPerPage = 5;
  const totalPages = Math.max(1, Math.ceil(filteredNews.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const itemsToDisplay = filteredNews.slice(startIndex, endIndex);

  useEffect(() => {
    if (isLoading) {
      (async () => {
        try {
          const {
            data: { news: remoteNews },
          } = await NewsApi.fetchNews();

          const modifiedNews = (remoteNews || []).map((n) => ({
            ...n,
            timeAgo: moment().from(n.createdAt, true),
          }));

          const sortedNews = sortNewsByDisplayOrder(modifiedNews);
          setNews(sortedNews);
          setFilteredNews(sortedNews);
        } catch (errorResponse) {
          const e =
            (errorResponse &&
              errorResponse.data &&
              errorResponse.data.message) ||
            (errorResponse && errorResponse.statusText) ||
            'Error al obtener las novedades';
          setError(e);
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [isLoading]);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const onChange = ({ target }) => {
    const value = target.value || '';
    const normalized = value.trim().toLowerCase();

    if (!normalized) {
      setFilteredNews(news);
      setCurrentPage(1);
      return;
    }

    const filteredList = news.filter((n) => {
      const titleMatch = (n.title || '').toLowerCase().includes(normalized);
      const dateMatch = new Date(n.createdAt)
        .toLocaleString()
        .toLowerCase()
        .includes(normalized);

      return titleMatch || dateMatch;
    });

    const sortedFiltered = sortNewsByDisplayOrder(filteredList);
    setFilteredNews(sortedFiltered);
    setCurrentPage(1);
  };

  const actionStrategy = (action, newsItem) => {
    switch (action) {
      case 'active':
        setModalContent({
          header: 'Activar Publicacion',
          content: 'Estas seguro que deseas activar la publicacion?',
          action,
          news: newsItem,
        });
        break;

      case 'draft':
        setModalContent({
          header: 'Desactivar Publicacion',
          content:
            'Estas seguro que deseas desactivar la publicacion? Quedara en modo borrador.',
          action,
          news: newsItem,
        });
        break;
      case 'deleted':
        setModalContent({
          header: 'Eliminar Publicacion',
          content: 'Estas seguro que deseas eliminar la publicacion?',
          action,
          news: newsItem,
        });
        break;

      default:
        break;
    }
  };

  const onClickAction = async (action, newsItem) => {
    setOpen(true);
    actionStrategy(action, newsItem);
  };

  const handleAccept = async () => {
    try {
      await NewsApi.updateNewsStatus(modalContent.news.id, modalContent.action);
      setOpen(false);
    } catch (errorResponse) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const e =
        (errorResponse && errorResponse.data && errorResponse.data.message) ||
        (errorResponse && errorResponse.statusText) ||
        'Error al actualizar la publicacion';
      setError(e);
    } finally {
      setIsLoading(true);
    }
  };

  return (
    <>
      <div className="user-container content-panel news__container">
        {error && <Alert type="error">{error}</Alert>}
        <HeadingSection
          title="Novedades"
          subtitle="Acá podrás administrar, crear, editar y eliminar publicaciones de la plataforma ChileConverge."
        />
        <div
          className="news__action"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', gap: 24 }}>
            <StrongSubtitle text="Publicaciones" variant="subtitle--big" />
            <Search onChange={onChange} />
          </div>
          <Button
            type="button"
            primary
            onClick={() => history.push('/novedades/crear', { mode: 'create' })}
          >
            Crear nueva publicacion
          </Button>
        </div>

        <div className="search-paginator">
          <span>Páginas </span>
          <Pagination
            className="inline-block"
            count={totalPages}
            page={Math.min(currentPage, totalPages)}
            onChange={(event, page) => setCurrentPage(page)}
          />
        </div>

        {!isLoading &&
          itemsToDisplay.map((newsItem) => (
            <NewsCard
              key={newsItem.id}
              news={newsItem}
              onClickAction={onClickAction}
              setError={setError}
            />
          ))}
        {!isLoading && itemsToDisplay.length === 0 && (
          <div className="news__empty">No se encontraron publicaciones.</div>
        )}
      </div>
      <NewsModal
        selectedNews={modalContent.news}
        content={modalContent}
        onClose={() => setOpen(!open)}
        open={open}
        handleAccept={handleAccept}
        handleDenied={() => setOpen(false)}
      />
      <div className="search-paginator">
        <span>Páginas </span>
        <Pagination
          className="inline-block"
          count={totalPages}
          page={Math.min(currentPage, totalPages)}
          onChange={(event, page) => setCurrentPage(page)}
        />
      </div>
    </>
  );
};

export default NewsList;
